import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { API_URL, formatRut, rutNorm, fechaToISO, isoToFecha, formatFechaLarga, getDiaNombre } from '../constants';

const NOMBRES_MES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const DIAS_LABEL  = ['L','M','X','J','V','S','D'];

export default function ProfesorApp({ entrenadores, reservas, alumnos, onCancelarReserva }) {

  // ── Auth ──────────────────────────────────────────────────────────
  const [profesor, setProfesor]   = useState(null);
  const [rutInput, setRutInput]   = useState('');
  const [loginError, setLoginError] = useState('');

  // ── Calendario ────────────────────────────────────────────────────
  const [calMes, setCalMes] = useState(() => {
    const h = new Date(); return { y: h.getFullYear(), m: h.getMonth() };
  });
  const [diaSeleccionado, setDiaSeleccionado] = useState(null);
  const [modalSesion, setModalSesion]         = useState(null);
  const [modalReagendar, setModalReagendar]   = useState(null);
  const [nuevaFecha, setNuevaFecha]           = useState('');
  const [nuevaHora, setNuevaHora]             = useState('');

  // ── Login ─────────────────────────────────────────────────────────
  const handleLogin = () => {
    const norm = rutNorm(rutInput);
    if (!norm) { setLoginError('Ingresa tu RUT.'); return; }
    const found = entrenadores.find(e => rutNorm(e.rut || '') === norm);
    if (found) {
      setProfesor(found);
    } else {
      setLoginError('RUT no encontrado. Verifica con recepción.');
    }
  };

  // ── Reservas del profesor ─────────────────────────────────────────
  const misReservas = useMemo(() => {
    if (!profesor) return [];
    return (reservas || [])
      .filter(r => r.profesor_id === profesor.id)
      .map(r => ({
        id: r.id,
        alumno_id: r.alumno_id,
        alumnoNombre: r.alumno_nombre || (() => {
          const a = alumnos?.find(a => a.id === r.alumno_id);
          return a ? `${a.nombre} ${a.apellido}` : 'Alumno';
        })(),
        alumnoTel: alumnos?.find(a => a.id === r.alumno_id)?.telefono || null,
        fecha: r.fecha_reserva,
        hora: r.hora_inicio?.slice(0, 5),
        horaFin: r.hora_fin?.slice(0, 5),
        completada: r.completada || false,
      }));
  }, [profesor, reservas, alumnos]);

  // ── Reservas del día seleccionado ─────────────────────────────────
  const reservasDia = useMemo(() => {
    if (!diaSeleccionado) return [];
    return misReservas
      .filter(r => r.fecha === diaSeleccionado)
      .sort((a, b) => a.hora.localeCompare(b.hora));
  }, [misReservas, diaSeleccionado]);

  // ── Días del mes con reservas ─────────────────────────────────────
  const diasConReservas = useMemo(() => {
    const set = new Set(misReservas.map(r => r.fecha));
    return set;
  }, [misReservas]);

  // ── Celdas del calendario ─────────────────────────────────────────
  const calCeldas = useMemo(() => {
    const primerDia = new Date(calMes.y, calMes.m, 1);
    const totalDias = new Date(calMes.y, calMes.m + 1, 0).getDate();
    let offset = primerDia.getDay() - 1;
    if (offset < 0) offset = 6;
    const celdas = [];
    for (let i = 0; i < offset; i++) celdas.push(null);
    for (let d = 1; d <= totalDias; d++) celdas.push(d);
    return celdas;
  }, [calMes]);

  const hoyISO = fechaToISO(new Date());

  // ── Acciones ──────────────────────────────────────────────────────
  const handleCancelar = async (id) => {
    if (!window.confirm('¿Cancelar esta sesión?')) return;
    onCancelarReserva && onCancelarReserva(id);
    setModalSesion(null);
  };

  const handleMarcarCompletada = async (id) => {
    try {
      await axios.put(`${API_URL}/reservas/${id}`, { completada: true });
      setModalSesion(null);
    } catch (err) {
      alert('Error al marcar como completada: ' + err.message);
    }
  };

  const handleReagendar = async () => {
    if (!nuevaFecha || !nuevaHora) { alert('Selecciona fecha y hora.'); return; }
    try {
      await axios.put(`${API_URL}/reservas/${modalReagendar.id}`, {
        fecha_reserva: nuevaFecha,
        hora_inicio: nuevaHora,
      });
      setModalReagendar(null);
      setNuevaFecha('');
      setNuevaHora('');
      alert('✅ Sesión reagendada.');
    } catch (err) {
      alert('Error al reagendar: ' + err.message);
    }
  };

  // ════════════════════════════════════════════════════════════════
  // RENDER — LOGIN
  // ════════════════════════════════════════════════════════════════
  if (!profesor) return (
    <div className="ar-root">
      <div className="ar-login-card">
        <div className="ar-login-logo">∞ INFINITY <span>CENTER</span></div>
        <div className="ar-login-sub">Portal del Entrenador</div>
        <div className="ar-login-icon">🏋️</div>
        <div className="ar-login-title">Inicia sesión</div>
        <div className="ar-login-hint">Ingresa tu RUT para ver tu agenda</div>
        <div className="ar-login-field">
          <label className="ar-label">Tu RUT</label>
          <input
            className="ar-input"
            placeholder="12.345.678-9"
            value={rutInput}
            maxLength={12}
            onChange={e => { setRutInput(formatRut(e.target.value)); setLoginError(''); }}
            onKeyDown={e => e.key === 'Enter' && handleLogin()}
          />
        </div>
        {loginError && <div className="ar-error">{loginError}</div>}
        <button className="ar-btn-primary" onClick={handleLogin}>Ingresar →</button>
      </div>
    </div>
  );

  // ════════════════════════════════════════════════════════════════
  // RENDER — AGENDA
  // ════════════════════════════════════════════════════════════════
  return (
    <div className="ar-root">
      {/* Top bar */}
      <div className="ar-topbar">
        <div className="ar-topbar-info">
          <div className="ar-topbar-avatar" style={{ background: profesor.color }}>
            {profesor.nombre?.[0]}
          </div>
          <div>
            <div className="ar-topbar-name">{profesor.nombre}</div>
            <div className="ar-topbar-plan" style={{ color: 'var(--cyan)' }}>
              {misReservas.length} sesiones este mes
            </div>
          </div>
        </div>
        <button className="ar-logout-btn" onClick={() => { setProfesor(null); setRutInput(''); }}>Salir</button>
      </div>

      <div className="ar-content">
        <div className="ar-screen">

          {/* Calendario mensual */}
          <div className="ar-cal-wrap">
            <div className="ar-cal-header">
              <button className="ar-cal-nav" onClick={() => setCalMes(p => ({ y: p.m === 0 ? p.y - 1 : p.y, m: p.m === 0 ? 11 : p.m - 1 }))}>‹</button>
              <span className="ar-cal-titulo">{NOMBRES_MES[calMes.m]} {calMes.y}</span>
              <button className="ar-cal-nav" onClick={() => setCalMes(p => ({ y: p.m === 11 ? p.y + 1 : p.y, m: p.m === 11 ? 0 : p.m + 1 }))}>›</button>
            </div>
            <div className="ar-cal-grid">
              {DIAS_LABEL.map(d => <div key={d} className="ar-cal-dow">{d}</div>)}
              {calCeldas.map((d, i) => {
                if (!d) return <div key={`e${i}`} />;
                const fecha = fechaToISO(new Date(calMes.y, calMes.m, d));
                const tieneReservas = diasConReservas.has(fecha);
                const esHoy = fecha === hoyISO;
                const esSeleccionado = diaSeleccionado === fecha;
                const count = misReservas.filter(r => r.fecha === fecha).length;
                return (
                  <button
                    key={fecha}
                    onClick={() => setDiaSeleccionado(fecha === diaSeleccionado ? null : fecha)}
                    className={`ar-cal-dia prof${esSeleccionado ? ' selected' : ''}${esHoy ? ' hoy' : ''}${tieneReservas ? ' con-reservas' : ''}`}
                  >
                    {d}
                    {tieneReservas && <span className="ar-cal-badge">{count}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Lista de sesiones del día seleccionado */}
          {diaSeleccionado && (
            <div className="ar-section" style={{ marginTop: 16 }}>
              <div className="ar-section-title">
                {getDiaNombre(diaSeleccionado)} {isoToFecha(diaSeleccionado).getDate()} de {NOMBRES_MES[isoToFecha(diaSeleccionado).getMonth()]}
              </div>
              {reservasDia.length === 0
                ? <div className="ar-empty" style={{ padding: '20px 0' }}>
                    <div>Sin sesiones este día</div>
                  </div>
                : reservasDia.map(r => (
                    <div
                      key={r.id}
                      className="ar-reserva-card"
                      style={{ borderLeft: `4px solid ${profesor.color}`, cursor: 'pointer' }}
                      onClick={() => setModalSesion(r)}
                    >
                      <div className="ar-reserva-top">
                        <div>
                          <div className="ar-reserva-ent" style={{ color: profesor.color }}>
                            {r.hora} — {r.horaFin || '—'}
                          </div>
                          <div className="ar-reserva-tiempo">👤 {r.alumnoNombre}</div>
                          {r.alumnoTel && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>📱 {r.alumnoTel}</div>}
                        </div>
                        {r.completada && <span style={{ fontSize: 11, color: 'var(--green)', fontWeight: 700 }}>✓ Completada</span>}
                      </div>
                    </div>
                  ))
              }
            </div>
          )}

          {/* Próximas sesiones si no hay día seleccionado */}
          {!diaSeleccionado && (
            <div className="ar-section" style={{ marginTop: 16 }}>
              <div className="ar-section-title">Próximas sesiones</div>
              {misReservas
                .filter(r => r.fecha >= hoyISO)
                .sort((a, b) => a.fecha.localeCompare(b.fecha) || a.hora.localeCompare(b.hora))
                .slice(0, 5)
                .map(r => (
                  <div
                    key={r.id}
                    className="ar-sesion-card"
                    style={{ cursor: 'pointer' }}
                    onClick={() => { setDiaSeleccionado(r.fecha); setModalSesion(r); }}
                  >
                    <div className="ar-sesion-dot" style={{ background: profesor.color }} />
                    <div className="ar-sesion-info">
                      <div className="ar-sesion-ent">{r.alumnoNombre}</div>
                      <div className="ar-sesion-tiempo">{formatFechaLarga(r.fecha)} · {r.hora}</div>
                    </div>
                    <div className="ar-sesion-badge">PT</div>
                  </div>
                ))
              }
              {misReservas.filter(r => r.fecha >= hoyISO).length === 0 && (
                <div className="ar-empty" style={{ padding: '20px 0' }}>Sin sesiones próximas</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal detalle sesión */}
      {modalSesion && (
        <div className="modal-overlay-agenda" onClick={() => setModalSesion(null)}>
          <div className="modal-agenda" onClick={e => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={() => setModalSesion(null)}>✕</button>
            <div className="modal-title-agenda">Sesión PT</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {[
                ['Alumno',  modalSesion.alumnoNombre],
                ['Día',     formatFechaLarga(modalSesion.fecha)],
                ['Horario', `${modalSesion.hora} – ${modalSesion.horaFin || '—'}`],
                ...(modalSesion.alumnoTel ? [['Teléfono', modalSesion.alumnoTel]] : []),
              ].map(([k, v]) => (
                <div key={k} className="modal-row-agenda">
                  <span className="modal-key-agenda">{k}</span>
                  <span className="modal-val-agenda">{v}</span>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
              {!modalSesion.completada && (
                <button className="btn-modal-agenda primary" onClick={() => handleMarcarCompletada(modalSesion.id)}>
                  ✓ Marcar como completada
                </button>
              )}
              <button className="btn-modal-agenda" onClick={() => { setModalReagendar(modalSesion); setModalSesion(null); }}>
                📅 Reagendar
              </button>
              <button className="btn-modal-agenda danger" onClick={() => handleCancelar(modalSesion.id)}>
                🗑 Cancelar sesión
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal reagendar */}
      {modalReagendar && (
        <div className="modal-overlay-agenda" onClick={() => setModalReagendar(null)}>
          <div className="modal-agenda" onClick={e => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={() => setModalReagendar(null)}>✕</button>
            <div className="modal-title-agenda">Reagendar sesión</div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>
              Alumno: <strong style={{ color: 'var(--text)' }}>{modalReagendar.alumnoNombre}</strong>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 5 }}>Nueva fecha</div>
                <input type="date" className="sel-agenda" value={nuevaFecha} min={hoyISO}
                  onChange={e => setNuevaFecha(e.target.value)} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 5 }}>Nueva hora</div>
                <input type="time" className="sel-agenda" value={nuevaHora}
                  onChange={e => setNuevaHora(e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button className="btn-modal-agenda" onClick={() => setModalReagendar(null)}>Cancelar</button>
              <button className="btn-modal-agenda primary" onClick={handleReagendar}>✓ Confirmar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
